import React, { useEffect, useMemo } from "react";

import slugify from "@sindresorhus/slugify";

import ScrollTabs from "./scroll-tabs";

import useScrollTabs from "../hooks/use-scroll-tabs";

import LiferayComponent from "../components/liferay-component";
import { normalizeRelativePath } from "../utility/paths";

const PageContentBody = ({
  page,
  location,
  config,
  labelManagerLabels,
  origin,
  showTabs = true,
  showBreadcrumb = true,
  setIsAnchor,
  isNewMenu,
}) => {
  const breadcrumb = useMemo(
    () =>
      [
        {
          name: labelManagerLabels.home,
          link: "/",
        },
      ].concat(
        page.parentPages?.length > 0
          ? page.parentPages?.map((item) => ({
              name:
                isNewMenu && item.menuItem?.useCustomName && item.menuItem?.name
                  ? item.menuItem.name
                  : item.page.liferayFields.title,
              link: item.page.slug
                ? normalizeRelativePath(item.page.slug)
                : item.page.externalLink || "",
            }))
          : []
      ),
    [page, labelManagerLabels, isNewMenu]
  );
  const sections = page.contentBody;
  // Il primo elemento è sempre l'"hero" (va prima dei tab)
  const hero = useMemo(() => sections[0], [sections]);
  // Gli altri elementi vengono dopo e potenzialmente popolano i tab
  const bodyItems = useMemo(() => sections.slice(1).filter((item) => !!item), [sections]);
  const tabs = useMemo(() => {
    return (
      bodyItems
        .map((item) =>
          item.moduleTitle || item.assistanceFooterModule || item.moduleTitleFieldSet
            ? item.moduleTitle || item.assistanceFooterModule || item.moduleTitleFieldSet
            : [item]
        )
        .flat()
        .filter((module) => {
          return module?.anchorDesktop
            ? module.anchorDesktop
            : module?.anchor?.anchorTitleDesktopPush
            ? module.anchor.anchorTitleDesktopPush
            : module?.anchorDesktop
            ? module.anchorDesktop
            : module?.anchorTitleDesktop
            ? module.anchorTitleDesktop
            : module?.anchor?.anchorDesktop
            ? module.anchor.anchorDesktop
            : null;
        })
        // Filtro componenti che contengono prodotti ma per cui non ne esiste nemmeno uno
        .filter(
          (module) =>
            !module?.SKU ||
            module?.product?.length ||
            !module?.skusText ||
            module?.products?.some((item) => item.key_prod)
        )
        .map((module) => {
          const anchorDesktop = module?.anchorDesktop
            ? module.anchorDesktop
            : module?.anchor?.anchorTitleDesktopPush
            ? module.anchor.anchorTitleDesktopPush
            : module?.anchorTitleDesktop
            ? module.anchorTitleDesktop
            : module?.anchor?.anchorDesktop
            ? module.anchor.anchorDesktop
            : module.anchorDesktop;
          const anchorMobile = module ? module.anchorMobile : module?.anchorMobile;
          return {
            id: slugify(anchorDesktop),
            label: anchorDesktop,
            labelMobile: anchorMobile || anchorDesktop,
          };
        })
    );
  }, [bodyItems]);
  const sectionIds = useMemo(() => tabs.map((tab) => tab.id), [tabs]);
  const [activeTab, tabsRef, tabsHeight] = useScrollTabs(sectionIds);

  useEffect(() => {
    if (showTabs && !!tabs.length) {
      setIsAnchor(true);
    } else {
      setIsAnchor(false);
    }
  });

  return (
    <div style={{ "--scroll-tabs-height": tabsHeight ? `${tabsHeight}px` : null }}>
      {hero ? (
        <LiferayComponent
          data={hero}
          config={config}
          labelManagerLabels={labelManagerLabels}
          isFirst
          breadcrumb={showBreadcrumb ? breadcrumb : null}
          location={location}
          pagePath={page?.slug}
          origin={origin}
        />
      ) : null}
      {page?.chatSticky?.liferayFields ? (
        <a
          href={page.chatSticky?.link}
          target="_blank"
          rel="noopener noreferrer"
          className="chat-icon"
        >
          <img
            src={page.chatSticky?.icon?.node?.publicURL}
            alt={page.chatSticky.liferayFields.title || "Chat"}
          />
        </a>
      ) : null}
      {showTabs && !!tabs.length && (
        <ScrollTabs ref={tabsRef} tabs={tabs} activeTab={activeTab} sticky={true} />
      )}
      {bodyItems.map((item, i) => (
        <LiferayComponent
          data={item}
          config={config}
          labelManagerLabels={labelManagerLabels}
          location={location}
          origin={origin}
          pagePath={page?.slug}
          key={item.id || i}
        />
      ))}
    </div>
  );
};

export default PageContentBody;
